import React from 'react';
import './StyledInput.css';

/**
 * StyledInput — Standardized input component
 * 
 * @param {string} label
 * @param {string} helperText
 * @param {string} error - Error message (shows error state)
 * @param {'text'|'email'|'password'|'number'|'search'|'tel'|'url'} type
 * @param {'sm'|'md'|'lg'} size
 * @param {boolean} fullWidth
 * @param {boolean} disabled
 * @param {React.ReactNode} startIcon
 * @param {React.ReactNode} endIcon
 * @param {boolean} required
 */
const StyledInput = ({
  label,
  helperText,
  error,
  type = 'text',
  size = 'md',
  fullWidth = true,
  disabled = false,
  startIcon,
  endIcon,
  required = false,
  className = '',
  id,
  ...props
}) => {
  const inputId = id || `ck-input-${label?.replace(/\s+/g, '-').toLowerCase() || Math.random().toString(36).substr(2, 9)}`;

  const wrapperClasses = [
    'ck-input-wrapper',
    fullWidth && 'ck-input-wrapper--full',
    error && 'ck-input-wrapper--error',
    disabled && 'ck-input-wrapper--disabled',
    className,
  ].filter(Boolean).join(' ');

  const fieldClasses = [
    'ck-input-field',
    `ck-input-field--${size}`,
    startIcon && 'ck-input-field--has-start-icon',
    endIcon && 'ck-input-field--has-end-icon',
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={inputId} className="ck-input-label">
          {label}
          {required && <span className="ck-input-required">*</span>}
        </label>
      )}
      <div className="ck-input-container">
        {startIcon && <span className="ck-input-icon ck-input-icon--start">{startIcon}</span>}
        <input
          id={inputId}
          type={type}
          className={fieldClasses}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
          {...props}
        />
        {endIcon && <span className="ck-input-icon ck-input-icon--end">{endIcon}</span>}
      </div>
      {error && <span id={`${inputId}-error`} className="ck-input-error">{error}</span>}
      {!error && helperText && <span id={`${inputId}-helper`} className="ck-input-helper">{helperText}</span>}
    </div>
  );
};

export default StyledInput;
